import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, Loader2, AlertCircle, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { API } from "../config/api";

export default function AddPaymentPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const status = searchParams.get('status');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestInfo, setRequestInfo] = useState(null);

  useEffect(() => {
    if (requestId) {
      axios.get(`${API}/request/${requestId}`).then(res => {
        setRequestInfo(res.data?.request);
      }).catch(() => {});
    }
  }, [requestId]);

  const handleAddPayment = async () => {
    if (!requestId) { setError("No request ID provided"); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/stripe/setup-link/${requestId}`);
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("Failed to create payment setup link");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to set up payment. Please try again or contact us.");
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Method Added!</h2>
            <p className="text-slate-600 mb-6">
              Your payment method has been saved successfully. You can close this page.
            </p>
            <p className="text-sm text-slate-500">
              Make sure you've also completed the other steps from your email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Setup Cancelled</h2>
            <p className="text-slate-600 mb-6">
              No worries! You can try again whenever you're ready.
            </p>
            <Button onClick={handleAddPayment} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Add Payment Method</h2>
          {requestInfo && (
            <p className="text-slate-500 text-sm mb-2">
              Request #{requestId} - {requestInfo.user_name}
            </p>
          )}
          <p className="text-slate-600 mb-6">
            Click below to securely add your payment method via Stripe. Supports credit/debit cards, Apple Pay, and Google Pay.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button onClick={handleAddPayment} disabled={loading} size="lg" className="w-full">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting to Stripe...</>
            ) : (
              <><CreditCard className="w-4 h-4 mr-2" />Add Payment Method</>
            )}
          </Button>
          <p className="text-xs text-slate-400 mt-4">Secured by Stripe. We never see your card details.</p>
        </CardContent>
      </Card>
    </div>
  );
}
